export const MIN_EXCEL_COLUMN_WIDTH = 48
export const MAX_EXCEL_COLUMN_WIDTH = 640
export const MAX_EXCEL_AUTOFIT_WIDTH = 420

export function clampExcelColumnWidth(width: number): number {
  return Math.max(MIN_EXCEL_COLUMN_WIDTH, Math.min(MAX_EXCEL_COLUMN_WIDTH, Math.round(width)))
}

export function retainExcelColumnWidths(
  current: Readonly<Record<string, number>>,
  next: Record<string, number>,
): Record<string, number> {
  const currentKeys = Object.keys(current)
  const nextKeys = Object.keys(next)
  if (currentKeys.length !== nextKeys.length) return next
  return currentKeys.every((key) => current[key] === next[key]) ? current as Record<string, number> : next
}

export function autoFitExcelColumnWidth(contentWidth: number): number {
  return Math.max(MIN_EXCEL_COLUMN_WIDTH, Math.min(MAX_EXCEL_AUTOFIT_WIDTH, Math.ceil(contentWidth)))
}

export function readExcelColumnWidths(raw: string | null, columnKeys: readonly string[]): Record<string, number> {
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const validKeys = new Set(columnKeys)
    return Object.fromEntries(Object.entries(parsed).flatMap(([key, value]) =>
      validKeys.has(key) && typeof value === 'number' && Number.isFinite(value)
        ? [[key, clampExcelColumnWidth(value)]]
        : [],
    ))
  } catch {
    return {}
  }
}

export function computeStickyOffsets<T extends { key: string; stickyLeft?: number; stickyRight?: number; width?: number | string }>(
  columns: readonly T[], widths: Readonly<Record<string, number>>,
): Record<string, { left?: number; right?: number }> {
  const result: Record<string, { left?: number; right?: number }> = {}
  let left = 0
  for (const column of columns) {
    if (column.stickyLeft === undefined) continue
    result[column.key] = { left }
    left += widths[column.key] ?? (typeof column.width === 'number' ? column.width : Number.parseFloat(String(column.width ?? '100')) || 100)
  }
  let right = 0
  for (const column of [...columns].reverse()) {
    if (column.stickyRight === undefined) continue
    result[column.key] = { ...result[column.key], right }
    right += widths[column.key] ?? (typeof column.width === 'number' ? column.width : Number.parseFloat(String(column.width ?? '100')) || 100)
  }
  return result
}
